<i18n
  src="@/i18n/components/client-settings/coordination/event/coordination-event-edit-table.json"
></i18n>
<template>
  <div class="eventEditTable">
    <SettingsTable>
      <SettingsTableRow>
        <SettingsTableCell
          v-t="'targetData'"
          width="200px"
          :style="{ padding: '20px 10px 20px 20px' }"
        />
        <SettingsTableCell width="480px">
          {{ coordinationEvent.targetData }}
        </SettingsTableCell>
      </SettingsTableRow>
      <SettingsTableRow>
        <SettingsTableCell
          width="200px"
          :style="{ padding: '20px 10px 20px 20px' }"
        >
          {{ $t("event") }}
          <span class="eventEditTableCell__required">{{ $t("required") }}</span>
        </SettingsTableCell>
        <SettingsTableCell width="480px">
          <template v-if="!eventLimitReached">
            <RadioButton
              :value="coordinationEvent.creationMethod"
              :label="CreationMethod.NEW"
              @change="onSelectCreationMethod"
              >{{ $t("newEvent") }}
            </RadioButton>
            <InputText
              :value="coordinationEvent.eventName"
              class="eventEditTable__eventMethodContent"
              :placeholder="$t('eventPlaceholder')"
              :max-length="40"
              @input="onInputEventName"
            />
            <br />
          </template>
          <RadioButton
            :value="coordinationEvent.creationMethod"
            :label="CreationMethod.EXISTING"
            @change="onSelectCreationMethod"
            >{{ $t("existingEvent") }}
          </RadioButton>
          <br />
          <SelectBox
            :value="coordinationEvent.eventId"
            :options="selectEventOptions"
            class="eventEditTable__eventMethodContent"
            @input="onSelectEvent"
          />
          <div
            v-if="eventLimitReached"
            class="eventEditTable__eventLimitText"
            v-html="eventLimitText"
          />
        </SettingsTableCell>
      </SettingsTableRow>
      <SettingsTableRow>
        <SettingsTableCell
          class="eventEditTable__inputParameterHeader"
          :style="{ padding: '20px 10px 20px 20px' }"
        >
          <span v-t="'coordinationParameter'" />
          <Tooltip :placement="tooltipPlacement.Bottom">
            <Icon
              :icon="icons.Help"
              :color="color.Base600"
              style="margin-left: 4px"
            />
            <div
              slot="content"
              v-t="'eventParameterTooltipText'"
              class="eventEditTable__tooltipContent"
            />
          </Tooltip>
          <span class="eventEditTableCell__required">{{ $t("required") }}</span>
        </SettingsTableCell>
        <SettingsTableCell>
          <div v-for="(param, index) in coordinationEvent.path" :key="index">
            <div class="eventEditTable__inputParameter">
              <InputText
                :value="param"
                class="eventEditTable__inputParameterForm"
                :placeholder="$t('eventParameterPlaceholder')"
                :max-length="40"
                :has-error="isValidCvParam(param)"
                @input="onInputParam($event, index)"
              />
              <IconButton
                v-show="numForm > 1"
                :icon="icons.ButtonClose"
                :size="18"
                @click="onClickDelete(index)"
              />
            </div>
            <div
              v-show="isValidCvParam(param)"
              v-t="'errorText'"
              class="eventEditTable__errorText"
            />
          </div>

          <div
            v-if="canAddParameter"
            class="eventEditTable__addParameter"
            @click="onClickNext"
          >
            <IconButton
              :size="18"
              :icon="icons.PlusCircle"
              class="eventEditTable__addParameterIcon"
            />
            <span v-t="'addEventParameter'" />
          </div>
          <div
            v-t="'parameterDescription'"
            class="eventEditTable__parameterDescription"
          />
        </SettingsTableCell>
      </SettingsTableRow>
    </SettingsTable>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Emit } from "vue-property-decorator";
import SettingsTable from "@/components/table/SettingsTable.vue";
import SettingsTableRow from "@/components/table/SettingsTableRow.vue";
import SettingsTableCell from "@/components/table/SettingsTableCell.vue";
import Icon from "@/components/Icon.vue";
import IconButton from "@/components/IconButton.vue";
import RadioButton from "@/components/form/RadioButton.vue";
import SelectOption from "@/components/form/SelectOption";
import SelectBox from "@/components/form/SelectBox.vue";
import InputText from "@/components/form/InputText.vue";
import Tooltip from "@/components/Tooltip.vue";
import { Colors } from "@/const/Colors";
import { Icons } from "@/const/Icons";
import { TooltipPlacement } from "@/const/tooltip";
import {
  EventDefinition,
  CoordinationEventEditableData,
  isInValidEventParam
} from "@/models/client-settings/EventDefinition";
import { CreationMethod } from "@/models/client-settings/Coordination";

const EMPTY_EVENT_VALUE = 0;

@Component({
  components: {
    Icon,
    IconButton,
    RadioButton,
    SelectBox,
    InputText,
    SettingsTable,
    SettingsTableRow,
    SettingsTableCell,
    Tooltip
  }
})
export default class CoordinationEventEditTable extends Vue {
  CreationMethod = CreationMethod;
  color = Colors;
  icons = Icons;
  tooltipPlacement = TooltipPlacement;

  @Prop({ type: Object, required: true })
  coordinationEvent!: CoordinationEventEditableData;

  @Prop({ type: Boolean, default: false })
  eventLimitReached!: boolean;

  @Prop({ type: Boolean, required: true })
  isContractWeb!: boolean;

  @Emit("update")
  updateCoordinationEvent(data: CoordinationEventEditableData) {
    return data;
  }

  get eventLimitText() {
    return this.isContractWeb
      ? this.$t("eventLimit")
      : this.$t("eventLimitAppOnly");
  }

  get activeEvents(): EventDefinition[] {
    return this.$store.state.clientSettings.activeEventDefinitions;
  }

  get selectEventOptions(): SelectOption[] {
    return [
      {
        label: "-----------",
        value: EMPTY_EVENT_VALUE,
        disabled: false
      },
      ...this.activeEvents.map(event => ({
        label: event.name,
        value: event.id,
        disabled: false
      }))
    ];
  }

  get numForm(): number {
    return this.coordinationEvent.path.length;
  }

  get canAddParameter() {
    return this.numForm < 5;
  }

  onInputEventName(value: string) {
    this.updateCoordinationEvent({
      ...this.coordinationEvent,
      eventName: value.trim()
    });
  }

  onSelectCreationMethod(value: CreationMethod) {
    this.updateCoordinationEvent({
      ...this.coordinationEvent,
      creationMethod: value
    });
  }

  onSelectEvent(value: string) {
    this.updateCoordinationEvent({
      ...this.coordinationEvent,
      eventId: Number(value)
    });
  }

  onInputParam(value: string, index: number) {
    const newParams = [];
    const currentParams = [...this.coordinationEvent.path];
    for (let i = 0; i < this.numForm; i++) {
      const newParam = i === index ? value.trim() : currentParams[i] || "";
      newParams.push(newParam);
    }

    this.updateCoordinationEvent({
      ...this.coordinationEvent,
      path: newParams
    });
  }

  onClickDelete(index: number) {
    const newParams = [...this.coordinationEvent.path];
    newParams.splice(index, 1);

    this.updateCoordinationEvent({
      ...this.coordinationEvent,
      path: newParams
    });
  }

  onClickNext() {
    this.updateCoordinationEvent({
      ...this.coordinationEvent,
      path: [...this.coordinationEvent.path, ""]
    });
  }

  isValidCvParam(path: string): boolean {
    return isInValidEventParam(path);
  }
}
</script>

<style lang="scss" scoped>
.eventEditTable {
  margin-bottom: 16px;
  width: 680px;
}
.eventEditTableCell__required {
  color: #c93636;
  display: inline;
  float: right;
  margin-left: 10px;
}
.eventEditTable__eventMethodContent {
  margin-left: 35px;
}
.eventEditTable__eventLimitText {
  margin-top: 12px;

  ::v-deep a {
    color: $colorBase700;
    text-decoration: underline;
    &:hover {
      color: $colorBase800;
    }
  }
}
.eventEditTable__tooltipContent {
  width: 226px;
  white-space: pre-line;
  word-wrap: break-word;
  line-height: 1.5;
}
.eventEditTable__inputParameterHeader > * {
  display: inline-flex;
  vertical-align: middle;
}
.eventEditTable__inputParameter {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}
.eventEditTable__inputParameterForm {
  width: 400px;
  margin-right: 8px;
}
.eventEditTable__errorText {
  margin-bottom: 10px;
  color: $colorError;
}
.eventEditTable__addParameter {
  display: inline-flex;
  font-size: 13px;
  align-items: center;
  cursor: pointer;
}
.eventEditTable__addParameterIcon {
  margin-right: 5px;
}
.eventEditTable__parameterDescription {
  margin-top: 6px;
  color: $colorBase700;
}
</style>
